import { DeviceMode, type NodeV2 } from '@vedalib/editor'
import cn from 'classnames'
import React, { useCallback } from 'react'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { ABSOLUTE_PORTAL_GAP } from 'components/Editor/components/AbsolutePortal/constants'
import KeyBindTag from 'components/Editor/components/KeyBindTag/KeyBindTag'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getNodeStyle } from 'services/Store/Project/selectors'
import type { Block, IBlockMode } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import { onSwap } from '../../commands'
import { onDrag } from '../../commands/onDrag'
import { isGrid } from '../../guards'
import { useContentPlus } from '../../useContentPlus'
import NodeV2CommandsMenu from '../NodeV2CommandsMenu'
import * as s from './NodeV2Label.module.scss'

interface INodeV2MultiLabelProps {
  mode: IBlockMode
  block: Block
  node: NodeV2
  selected: NodeV2[]
}

const NodeV2MultiLabel: React.FC<INodeV2MultiLabelProps> = (props) => {
  const { mode, block, node, selected } = props
  const isPro = mode.editorMode === AppMode.pro
  const styles = useProjectContext(getNodeStyle, block.uuid, node.id)
  const isContentPlus = useContentPlus()
  const canDrag =
    (isPro || isContentPlus) &&
    (mode.deviceMode === DeviceMode.desktop ||
      styles?.display === 'absolute' ||
      styles?.display === 'grid')

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      onDrag(event, {
        mode: mode.editorMode,
        blockId: block.uuid,
        source: node,
        targets: selected,
        isContentPlus,
      })
    },
    [mode.editorMode, block.uuid, node, selected, isContentPlus],
  )

  return (
    <AbsolutePortal
      chain='off'
      name='title'
      offset={`${-ABSOLUTE_PORTAL_GAP}px 0`}
      placement='topLeft'
      recalculatingKey={block}
      translateY='-100%'
    >
      <div className={s.multiLabel}>
        <div className={cn(s.item, s[styles?.display || 'element'], { [s.active]: !node?.isDone })}>
          {canDrag && (
            <Icon className={s.drag} name='drag' onMouseDown={handleMouseDown} size={KitSize.XS} />
          )}
          <Typography color='inherit' indents={{ padding: '0 2px' }} rows={1} size={KitSize.S}>
            {`${t('editorV2.selectedNodes')} (${selected.length})`}
          </Typography>
          {(isPro || isContentPlus) && (
            <NodeV2CommandsMenu
              block={block}
              color={styles?.display}
              mode={mode}
              node={node}
              selected={selected}
            />
          )}
        </div>
        {isGrid(styles) && selected.length === 2 && (
          <Tooltip
            mouseEnterDelay={0.5}
            overlay={
              <div className={s.tooltip}>
                {t('editorV2.grid.switchElements')}
                <KeyBindTag bind='shift+S' dark />
              </div>
            }
            placement='top'
          >
            <div className={cn(s.item, s.active, s[styles?.display || 'element'])}>
              <Icon name='switch' onClick={onSwap} size={KitSize.XS} />
            </div>
          </Tooltip>
        )}
      </div>
    </AbsolutePortal>
  )
}

export default React.memo(NodeV2MultiLabel)
